/**
 * Camlin Companion — minimal markdown renderer for avatar replies
 * (Keith, round 3, 6 Aug 2026: "render markdown in avatar replies —
 * bold, lists, links, code — sanitized, links opening in new tab").
 *
 * Deliberately NOT a full markdown engine and deliberately NOT an
 * HTML-string pipeline: everything is built as React elements, so there
 * is no innerHTML anywhere and nothing to sanitize post-hoc — script
 * injection is structurally impossible. Raw HTML in the source text
 * renders as literal text.
 *
 * Supported (round 5, 6 Aug 2026 — Keith saw raw "### Welcome" in a
 * bubble, so this now covers the full set the model actually emits):
 *   - **bold**, *italic* / _italic_, `inline code`
 *   - # … ###### headings (scaled for a chat bubble, not page-sized)
 *   - fenced code blocks ``` ... ```
 *   - [links](https://…) — http/https only, target="_blank" +
 *     rel="noopener noreferrer"
 *   - unordered (-, *, •) and ordered (1.) lists
 *   - | pipe | tables | with a |---| separator row
 *   - > blockquotes
 *   - horizontal rules (---, ***, ___)
 *   - paragraphs / single line breaks
 */

import React from 'react';

const LINK_RE = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/;
const BOLD_RE = /\*\*([^*]+)\*\*/;
const ITALIC_RE = /(?:\*([^*\n]+)\*|_([^_\n]+)_)/;
const CODE_RE = /`([^`]+)`/;

const codeStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '0.9em',
  background: 'rgba(127,127,127,0.18)',
  borderRadius: '4px',
  padding: '1px 4px'
};

const codeBlockStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '0.85em',
  background: 'rgba(127,127,127,0.15)',
  borderRadius: '8px',
  padding: '8px 10px',
  margin: '4px 0',
  overflowX: 'auto',
  whiteSpace: 'pre'
};

const listStyle: React.CSSProperties = {
  margin: '4px 0',
  paddingLeft: '20px'
};

// Chat-bubble heading scale: semantic h1–h6 elements, but sized for a
// bubble (an actual 2em h1 inside a 320px transcript looks broken).
const HEADING_SIZES = ['1.2em', '1.12em', '1.05em', '1em', '1em', '1em'];

const blockquoteStyle: React.CSSProperties = {
  margin: '4px 0',
  padding: '2px 10px',
  borderLeft: '3px solid rgba(127,127,127,0.45)',
  opacity: 0.9
};

const hrStyle: React.CSSProperties = {
  border: 'none',
  borderTop: '1px solid rgba(127,127,127,0.35)',
  margin: '8px 0'
};

const tableStyle: React.CSSProperties = {
  borderCollapse: 'collapse',
  margin: '6px 0',
  fontSize: '0.92em',
  width: '100%'
};

const tableCellStyle: React.CSSProperties = {
  border: '1px solid rgba(127,127,127,0.35)',
  padding: '3px 8px',
  textAlign: 'left',
  verticalAlign: 'top'
};

const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const HR_RE = /^\s*(?:-{3,}|\*{3,}|_{3,})\s*$/;
const TABLE_ROW_RE = /^\s*\|(.+)\|\s*$/;
const TABLE_SEPARATOR_RE = /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)+\|?\s*$/;

function splitTableRow(line: string): string[] {
  const inner = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return inner.split('|').map((cell) => cell.trim());
}

/** Inline pass: links, bold, italic, code — earliest match wins, recurse on the rest. */
function renderInline(text: string, keyPrefix: string): React.ReactNode[] {
  if (!text) {
    return [];
  }

  const candidates: Array<{ index: number; length: number; node: (inner: string, key: string) => React.ReactNode; match: RegExpMatchArray }> = [];

  const link = text.match(LINK_RE);
  if (link && link.index !== undefined) {
    candidates.push({
      index: link.index,
      length: link[0].length,
      match: link,
      node: (_inner, key) => (
        <a key={key} href={link[2]} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
          {renderInline(link[1], `${key}-l`)}
        </a>
      )
    });
  }
  const bold = text.match(BOLD_RE);
  if (bold && bold.index !== undefined) {
    candidates.push({
      index: bold.index,
      length: bold[0].length,
      match: bold,
      node: (_inner, key) => <strong key={key}>{renderInline(bold[1], `${key}-b`)}</strong>
    });
  }
  const italic = text.match(ITALIC_RE);
  if (italic && italic.index !== undefined) {
    candidates.push({
      index: italic.index,
      length: italic[0].length,
      match: italic,
      node: (_inner, key) => <em key={key}>{renderInline(italic[1] ?? italic[2] ?? '', `${key}-i`)}</em>
    });
  }
  const code = text.match(CODE_RE);
  if (code && code.index !== undefined) {
    candidates.push({
      index: code.index,
      length: code[0].length,
      match: code,
      // Inline code contents render literally — no nested formatting.
      node: (_inner, key) => <code key={key} style={codeStyle}>{code[1]}</code>
    });
  }

  if (candidates.length === 0) {
    return [text];
  }

  candidates.sort((a, b) => a.index - b.index);
  const first = candidates[0];
  const before = text.slice(0, first.index);
  const after = text.slice(first.index + first.length);
  return [
    ...(before ? [before] : []),
    first.node('', `${keyPrefix}-${first.index}`),
    ...renderInline(after, `${keyPrefix}-r${first.index + first.length}`)
  ];
}

/**
 * Block pass: fenced code, lists, paragraphs. Returns nodes suitable for a
 * chat bubble (compact margins).
 */
export function renderCompanionMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const blocks: React.ReactNode[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let codeBlock: string[] | null = null;
  let key = 0;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    const paraKey = `p${key += 1}`;
    blocks.push(
      <p key={paraKey} style={{ margin: '4px 0', whiteSpace: 'pre-wrap' }}>
        {paragraph.map((line, i) => (
          <React.Fragment key={`${paraKey}-${i}`}>
            {i > 0 && <br />}
            {renderInline(line, `${paraKey}-${i}`)}
          </React.Fragment>
        ))}
      </p>
    );
    paragraph = [];
  };

  const flushList = () => {
    if (!list) return;
    const items = list.items;
    const ListTag = list.ordered ? 'ol' : 'ul';
    const listKey = `li${key += 1}`;
    blocks.push(
      <ListTag key={listKey} style={listStyle}>
        {items.map((item, i) => (
          <li key={`${listKey}-${i}`} style={{ margin: '2px 0' }}>
            {renderInline(item, `${listKey}-${i}`)}
          </li>
        ))}
      </ListTag>
    );
    list = null;
  };

  for (let lineIndex = 0; lineIndex < lines.length; lineIndex++) {
    const line = lines[lineIndex] ?? '';

    if (codeBlock !== null) {
      if (line.trim().startsWith('```')) {
        blocks.push(<pre key={`c${key += 1}`} style={codeBlockStyle}>{codeBlock.join('\n')}</pre>);
        codeBlock = null;
      } else {
        codeBlock.push(line);
      }
      continue;
    }

    if (line.trim().startsWith('```')) {
      flushParagraph();
      flushList();
      codeBlock = [];
      continue;
    }

    const heading = line.match(HEADING_RE);
    if (heading) {
      flushParagraph();
      flushList();
      const level = heading[1].length;
      const headingKey = `h${key += 1}`;
      blocks.push(React.createElement(
        `h${level}`,
        {
          key: headingKey,
          style: {
            margin: '8px 0 4px',
            fontSize: HEADING_SIZES[level - 1],
            fontWeight: 700,
            lineHeight: 1.3
          }
        },
        renderInline(heading[2].trim(), headingKey)
      ));
      continue;
    }

    // Table: a pipe row whose NEXT line is the |---|---| separator. The
    // separator requirement keeps a stray "a | b" sentence out of table
    // rendering.
    if (TABLE_ROW_RE.test(line) && TABLE_SEPARATOR_RE.test(lines[lineIndex + 1] ?? '')) {
      flushParagraph();
      flushList();
      const headerCells = splitTableRow(line);
      const rows: string[][] = [];
      let cursor = lineIndex + 2;
      while (cursor < lines.length && TABLE_ROW_RE.test(lines[cursor] ?? '')) {
        rows.push(splitTableRow(lines[cursor] ?? ''));
        cursor++;
      }
      const tableKey = `t${key += 1}`;
      blocks.push(
        <table key={tableKey} style={tableStyle}>
          <thead>
            <tr>
              {headerCells.map((cell, i) => (
                <th key={`${tableKey}-h${i}`} style={{ ...tableCellStyle, fontWeight: 700 }}>
                  {renderInline(cell, `${tableKey}-h${i}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((cells, r) => (
              <tr key={`${tableKey}-r${r}`}>
                {headerCells.map((_, c) => (
                  <td key={`${tableKey}-r${r}c${c}`} style={tableCellStyle}>
                    {renderInline(cells[c] ?? '', `${tableKey}-r${r}c${c}`)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
      lineIndex = cursor - 1;
      continue;
    }

    if (HR_RE.test(line)) {
      flushParagraph();
      flushList();
      blocks.push(<hr key={`hr${key += 1}`} style={hrStyle} />);
      continue;
    }

    const quote = line.match(/^\s*>\s?(.*)$/);
    if (quote) {
      flushParagraph();
      flushList();
      const quoteLines: string[] = [quote[1]];
      let cursor = lineIndex + 1;
      while (cursor < lines.length) {
        const next = (lines[cursor] ?? '').match(/^\s*>\s?(.*)$/);
        if (!next) break;
        quoteLines.push(next[1]);
        cursor++;
      }
      const quoteKey = `q${key += 1}`;
      blocks.push(
        <blockquote key={quoteKey} style={blockquoteStyle}>
          {quoteLines.map((quoteLine, i) => (
            <React.Fragment key={`${quoteKey}-${i}`}>
              {i > 0 && <br />}
              {renderInline(quoteLine, `${quoteKey}-${i}`)}
            </React.Fragment>
          ))}
        </blockquote>
      );
      lineIndex = cursor - 1;
      continue;
    }

    const unordered = line.match(/^\s*[-*•]\s+(.*)$/);
    const ordered = line.match(/^\s*\d+[.)]\s+(.*)$/);
    if (unordered || ordered) {
      flushParagraph();
      const item = (unordered?.[1] ?? ordered?.[1] ?? '').trim();
      const isOrdered = Boolean(ordered);
      if (!list || list.ordered !== isOrdered) {
        flushList();
        list = { ordered: isOrdered, items: [] };
      }
      list.items.push(item);
      continue;
    }

    if (line.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }

    flushList();
    paragraph.push(line);
  }

  if (codeBlock !== null) {
    blocks.push(<pre key={`c${key += 1}`} style={codeBlockStyle}>{codeBlock.join('\n')}</pre>);
  }
  flushParagraph();
  flushList();

  // Single plain paragraph: skip the <p> wrapper so simple replies keep
  // the exact old bubble layout.
  if (blocks.length === 1 && paragraphOnly(text)) {
    return renderInline(text, 'solo');
  }

  return <>{blocks}</>;
}

function paragraphOnly(text: string): boolean {
  return !text.includes('\n')
    && !/^\s*[-*•]\s+/.test(text)
    && !/^\s*\d+[.)]\s+/.test(text)
    && !text.includes('```')
    && !HEADING_RE.test(text)
    && !HR_RE.test(text)
    && !/^\s*>/.test(text);
}

export default renderCompanionMarkdown;
